/**
 * IMPORTS
 */
import * as api from 'src/aggregates/conversations/api';
import selector from 'src/aggregates/conversations/selectors';
import {fetch as fetchPreferences} from 'src/aggregates/preferences/selectors';
import {fetch} from 'src/aggregates/user/selectors';
import {getServerUrl} from 'src/aggregates/user/selectors';
import {authenticate} from 'src/aggregates/user/utils';
import {start} from 'src/aggregates/user/utils';
import {endpoints} from 'src/aggregates/user/xmpp/main';
import timeout from 'src/aggregates/utils/timeout';
import {logger} from 'src/logger';
import {REGISTRATION} from 'src/serviceWorker/registration';
import Store from 'src/store';
import {subscribe} from 'src/utils/push';


/**
 * TYPES
 */
import {IHeartbeatResponse}
    from 'src/aggregates/conversations/session/session.d';
import {IOpenResponse} from 'src/aggregates/conversations/session/session.d';
import {IOpenResult} from 'src/aggregates/conversations/session/session.d';
import {Statuses} from 'src/aggregates/user/state.d';


/**
 * CONSTANTS AND DEFINITIONS
 */
const CONVERSIS = 'conversis@conference.platform.converja.com/conversis';
const DECIMALS = 0;
const HEARTBEAT_INTERVAL = 10000;
const TIMEOUT = 60000;
let REOPEN = false;

declare global
{
    interface Window
    {
        foreground: boolean;
    }
}


/**
 * CODE
 */

/**
 * I close session.
 *
 * :param uid: agent uid
 * :param token: session token
 *
 * :returns: promise with close result or null
 */
async function close (
    uid: string,
    token: string,
): Promise<void>
{
    // get agent and company
    const user = fetch();
    const agent = user?.name;
    const company = user?.company;

    // get current timestamp
    const t0 = performance.now();

    // conversis unavailable: log and fail
    if (endpoints.has(CONVERSIS) === false)
    {
        const timestamp = t0.toFixed(DECIMALS);
        logger.error('CVJPZ0015E', {agent, company, timestamp});
        return;
    }

    // conversis available: get endpoint
    const conversis = endpoints.get(CONVERSIS);

    // close session on conversis
    try
    {
        // log session close
        const timestamp = t0.toFixed(DECIMALS);
        logger.info('CVJPZ0014I', {agent, company, timestamp});

        // close session
        await timeout(
            conversis.request('DELETE',
                              `companies/${company}/sessions/${uid}`,
                              {token}),
            TIMEOUT,
        );
    }

    // close session failed: log failure
    catch (e)
    {
        // log elapsed time to heartbeat response
        const t1 = performance.now();
        logger.error('CVJPZ0016E',
                     {
                         agent,
                         company,
                         elapsed: (t1 - t0).toFixed(DECIMALS),
                         status: `${e.name}: ${e.message}`,
                         timestamp: t1.toFixed(DECIMALS),
                     });
    }
}


/**
 * I open session.
 *
 * :param uid: agent uid
 * :param company: agents company
 * :param name: agents name
 *
 * :returns: promise with open result or null
 */
async function open (
    uid: string,
    company: number,
    name: string,
): Promise<IOpenResult | null>
{
    // get current timestamp
    let t0 = performance.now();

    // conversis unavailable: log and abort
    if (endpoints.has(CONVERSIS) === false)
    {
        const timestamp = t0.toFixed(DECIMALS);
        logger.error('CVJPZ0010E', {agent: name, company, timestamp});
        return null;
    }

    // conversis available: get endpoint
    const conversis = endpoints.get(CONVERSIS);

    // trying to re-open connection: reconnect to xmpp
    if (REOPEN === true)
    {
        // get server url
        const url = getServerUrl();

        // log timestamp
        const timestamp = t0.toFixed(DECIMALS);
        logger.info('CVJPZ0011I', {
            agent: name,
            company,
            server: url,
            timestamp,
        });

        // connect to XMPP server
        const auth = await authenticate('', url, '');

        // connected successfully: enter rooms
        if (auth === null)
        {
            // log timestamp
            const timestamp = performance.now().toFixed(DECIMALS);
            logger.info('CVJPZ0012I', {agent: name, company, timestamp});

            // enter XMPP rooms
            const user = Store.getState().user;
            const result = await start(user.id, user.rooms);

            // failed to enter XMPP rooms: log and return null
            if (result === null)
            {
                const t1 = performance.now();
                logger.error('CVJPZ0013E',
                             {
                                 agent: name,
                                 company,
                                 elapsed: (t1 - t0).toFixed(DECIMALS),
                                 timestamp: t1.toFixed(DECIMALS),
                             });
                return null;
            }
        }

        // connection failed: log error and return null
        else
        {
            const t1 = performance.now();
            logger.error('CVJPZ0045E', {
                agent: name,
                company,
                elapsed: (t1 - t0).toFixed(DECIMALS),
                error: auth,
                timestamp: t1.toFixed(DECIMALS),
            });
            return null;
        }
    }

    // first time opening connection: set REOPEN to true
    else
    {
        REOPEN = true;
    }

    // initialize response
    let response: IOpenResponse;

    // get away status
    const user = fetch();
    const away = user.status === Statuses.AWAY;

    // get paused status
    const preferences = fetchPreferences(Number(user.id));
    const paused = preferences?.paused === true && away === true;

    // open session on conversis
    try
    {
        // log timestamp
        t0 = performance.now();
        const timestamp = t0.toFixed(DECIMALS);
        logger.info('CVJPZ0007I', {agent: name, company, timestamp});

        // open session
        response = await timeout(
            conversis.request(
                'POST',
                `companies/${company}/sessions/${uid}`,
                {away, name, paused},
            ),
            TIMEOUT,
        );
    }

    // open session failed: log and return null
    catch (e)
    {
        // log elapsed time to heartbeat response
        const t1 = performance.now();
        logger.error('CVJPZ0009E',
                     {
                         agent: name,
                         company,
                         elapsed: (t1 - t0).toFixed(DECIMALS),
                         status: `${e.name}: ${e.message}`,
                         timestamp: t1.toFixed(DECIMALS),
                     });
        return null;
    }

    // log elapsed time to heartbeat response
    const t1 = performance.now();

    // session could not be opened: return null
    if (response.status !== 201)
    {
        logger.error('CVJPZ0009E',
                     {
                         agent: name,
                         company,
                         elapsed: (t1 - t0).toFixed(DECIMALS),
                         status: response.status,
                         timestamp: t1.toFixed(DECIMALS),
                     });
        return null;
    }

    // log successfull session opening
    logger.info('CVJPZ0008I',
                {
                    agent: name,
                    company,
                    elapsed: (t1 - t0).toFixed(DECIMALS),
                    status: response.status,
                    timestamp: t1.toFixed(DECIMALS),
                });

    // get public key
    const publicKey = response.params.publicKey ?? null;

    // set default value for subscription
    let subscription: PushSubscription = null;

    // publicKey and registration are both not null: subscribe to push
    if (publicKey !== null && REGISTRATION !== null)
    {
        try
        {
            subscription = await subscribe(REGISTRATION.pushManager, publicKey);
        }

        // could not subscribe to push: log error
        catch (e)
        {
            logger.error('CVJPZ0054E', {company, reason: e, user: uid});
        }
    }

    // initialize heartbeat
    setTimeout(() => sendHeartbeat (uid, response.params.agent.session), 1000);

    // return session token
    return {
        push: {
            publicKey,
            subscription,
        },
        session: response.params.agent.session,
    };
}


/**
 * I send a heartbeat to conversis to check session status.
 *
 * :param uid: agent uid
 * :param token: session token
 *
 * :returns: promise with result
 */
async function sendHeartbeat (uid: string, token: string): Promise<void>
{
    // get agent, away status and company
    const user = fetch();
    const agent = user?.name;
    const away = user?.status == Statuses.AWAY;
    const company = user?.company;

    // get paused status
    const preferences = fetchPreferences(Number(user?.id));
    const paused = preferences?.paused === true;

    // get subscription
    const subscription = selector.getPushData()?.subscription?.toJSON() ?? null;

    // get current timestamp
    const t0 = performance.now();

    // conversis unavailable: return fail code
    if (endpoints.has(CONVERSIS) === false)
    {
        // log timestamp
        const timestamp = t0.toFixed(DECIMALS);
        logger.error('CVJPZ0006E', {agent, company, timestamp});

        // call api and abort
        api.onHeartbeatFailure();
        return;
    }

    // conversis available: get endpoint
    const conversis = endpoints.get(CONVERSIS);

    // initialize heartbeat response
    let response: IHeartbeatResponse;

    // send heartbeat
    try
    {
        // log timestamp
        const timestamp = t0.toFixed(DECIMALS);
        logger.info(
            'CVJPZ0002I',
            {
                agent,
                company,
                foreground: window.foreground ? 'True' : 'False',
                timestamp,
            },
        );

        // send heartbeat
        response = await timeout(
            conversis.request('PUT',
                              `/companies/${company}/agents/${uid}/heartbeat`,
                              {
                                  away,
                                  foreground: window.foreground,
                                  paused: away && paused,
                                  subscription,
                                  token,
                              }),
            TIMEOUT,
        );
    }

    // failed to send heartbeat: log, call on heartbeat failure api and return
    catch (e)
    {
        // log elapsed time to heartbeat response
        const t1 = performance.now();
        logger.error('CVJPZ0004E',
                     {
                         agent,
                         company,
                         elapsed: (t1 - t0).toFixed(DECIMALS),
                         status: `${e.name}: ${e.message}`,
                         timestamp: t1.toFixed(DECIMALS),
                     },
        );

        // call api and abort
        api.onHeartbeatFailure();
        return;
    }

    // successfull heartbeat: call on heartbeat success api and return
    if (response.status === 200)
    {
        // log elapsed time to heartbeat response
        const t1 = performance.now();
        logger.info('CVJPZ0003I',
                    {
                        agent,
                        company,
                        elapsed: (t1 - t0).toFixed(DECIMALS),
                        status: response.status,
                        timestamp: t1.toFixed(DECIMALS),
                    },
        );

        // get number of conversations on queue
        const conversationsOnQueue = response.params.conversationsOnQueue || 0;

        // call api and abort
        await api.onHeartbeatSuccess(conversationsOnQueue);
        setTimeout(() => sendHeartbeat (uid, token), HEARTBEAT_INTERVAL);
        return;
    }

    // heartbeat conflict: call on heartbeat conflict api and return
    else if (response.status === 409)
    {
        // log elapsed time to heartbeat response
        const t1 = performance.now();
        logger.error('CVJPZ0005E',
                     {
                         agent,
                         company,
                         elapsed: (t1 - t0).toFixed(DECIMALS),
                         timestamp: t1.toFixed(DECIMALS),
                     },
        );

        // call api and abort
        api.onHeartbeatConflict();
        return;
    }

    // log elapsed time to heartbeat response
    const t1 = performance.now();
    logger.error('CVJPZ0004E',
                 {
                     agent,
                     company,
                     elapsed: (t1 - t0).toFixed(DECIMALS),
                     status: response.status,
                     timestamp: t1.toFixed(DECIMALS),
                 },
    );

    // heartbeat failed: call on heartbeat failure api
    api.onHeartbeatFailure();
}


/**
 * EXPORTS
 */
export {
    close,
    open,
};
